import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi, type UserDto } from '../../api/users';
import { useReportScreen } from '../../components/ScreenContext';

/**
 * Admin: user management. The reason this page exists (MVP audit 2026-09-04):
 * accountants arriving via integravizija.lt SSO are auto-provisioned as
 * read-only Viewers — without this page the only way to let one actually WORK
 * was a direct database edit. Role change is one select; your own row is
 * locked (the API refuses self-demotion).
 */

const ROLES = ['admin', 'senior_accountant', 'accountant', 'viewer'] as const;

const ROLE_BADGE: Record<string, string> = {
  admin: 'bg-primary-100 text-primary-800',
  senior_accountant: 'bg-emerald-100 text-emerald-700',
  accountant: 'bg-sky-100 text-sky-700',
  viewer: 'bg-gray-100 text-gray-500',
};

export default function UsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) => usersApi.setRole(id, role),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => setError(err.message || t('users.roleChangeFailed')),
  });

  // Report clean query data (raw role values) to the copilot: the role cell is
  // a <select> whose option labels concatenate in DOM scans.
  const userCount = users?.length ?? 0;
  const screenPreview = useMemo(() =>
    (users ?? []).slice(0, 8).map((u: UserDto) =>
      `${u.username}: role=${u.role}${u.isActive ? ', aktyvus' : ', neaktyvus'}${u.isBridged ? ', SSO' : ''}${u.lastLoginAt ? ', lastLogin=' + new Date(u.lastLoginAt).toLocaleString('lt-LT') : ''}`.slice(0, 120)
    ), [users]);

  useReportScreen(`Naudotojai (${userCount})`, {
    recordCount: userCount,
    preview: screenPreview,
  });

  return (
    <div className="space-y-4">
      <div className="py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('users.title')}</h1>
        <p className="text-xs text-gray-400 mt-0.5">{t('users.subtitle')}</p>
      </div>

      {isLoading && <p className="text-sm text-gray-400 py-4">{t('common.loading')}</p>}

      {!isLoading && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 font-semibold">{t('users.col.user')}</th>
                <th className="px-4 py-2 font-semibold w-44">{t('users.col.role')}</th>
                <th className="px-4 py-2 font-semibold w-28">{t('users.col.status')}</th>
                <th className="px-4 py-2 font-semibold w-40">{t('users.col.lastLogin')}</th>
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((u: UserDto) => (
                <tr key={u.id} className="border-t border-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900">{u.username}</div>
                    <div className="text-xs text-gray-400">
                      {u.isBridged ? t('users.bridged') : t('users.local')}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <select
                        value={u.role}
                        disabled={roleMutation.isPending}
                        onChange={e => roleMutation.mutate({ id: u.id, role: e.target.value })}
                        className="border border-gray-200 rounded-lg px-2 py-1 text-sm bg-white"
                      >
                        {ROLES.map(r => <option key={r} value={r}>{t(`users.role.${r}`)}</option>)}
                      </select>
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${ROLE_BADGE[u.role] ?? ''}`}>
                        {u.role}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={u.isActive ? 'text-green-600' : 'text-gray-400'}>
                      {u.isActive ? '●' : '○'} {u.isActive ? t('users.active') : t('users.inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 tabular-nums">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('lt-LT') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(users ?? []).length === 0 && (
            <p className="text-sm text-gray-400 px-4 py-6 text-center">{t('users.empty')}</p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
      <p className="text-[11px] text-gray-400">{t('users.hint')}</p>
    </div>
  );
}
